import { useState, useEffect } from 'react'
import { useT } from '@/i18n'
import { api } from '@/services/api-client'
import { Wallet, ArrowUpCircle, ArrowDownCircle, Clock, DollarSign, TrendingUp, TrendingDown, FileText, CreditCard } from 'lucide-react'

interface WalletData {
  balance: number
  total_deposited: number
  total_spent: number
}

interface Transaction {
  id: string
  type: 'deposit' | 'usage' | 'leasing_income' | 'leasing_charge' | 'refund' | 'withdrawal'
  amount: number
  balance_after: number
  description: string
  created_at: string
}

const DEPOSIT_STEPS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 150, 200, 250, 300, 400, 500, 600, 700, 800, 900, 1000]

function getBonus(dollars: number): number {
  if (dollars >= 5000) return 1000
  if (dollars >= 1000) return 100
  if (dollars >= 500) return 40
  if (dollars >= 200) return 10
  return 0
}

export function WalletView() {
  const t = useT()
  const [wallet, setWallet] = useState<WalletData | null>(null)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [depositDollars, setDepositDollars] = useState(10)
  const [loading, setLoading] = useState(true)
  const [depositing, setDepositing] = useState(false)
  const [activeTab, setActiveTab] = useState<'deposit' | 'withdraw' | 'invoice'>('deposit')
  const [withdrawAmount, setWithdrawAmount] = useState('')
  const [invoiceData, setInvoiceData] = useState({
    wantInvoice: false,
    companyName: '',
    vatNumber: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
  })

  const fetchData = async () => {
    try {
      const [walletData, txData] = await Promise.all([
        api.getWallet(),
        api.getWalletTransactions(),
      ])
      setWallet(walletData)
      setTransactions(txData)
    } catch (err) {
      console.error('Failed to load wallet data:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleDeposit = async () => {
    if (depositDollars < 10) return
    setDepositing(true)
    try {
      const result = await api.depositWallet(depositDollars)
      if (result.checkout_url) {
        window.location.href = result.checkout_url
        return
      }
      await fetchData()
    } catch (err) {
      console.error('Deposit failed:', err)
    } finally {
      setDepositing(false)
    }
  }

  const handleWithdraw = async () => {
    const dollars = parseFloat(withdrawAmount)
    if (isNaN(dollars) || dollars < 10) return
    try {
      await api.withdrawWallet(dollars)
      setWithdrawAmount('')
      await fetchData()
    } catch (err) {
      console.error('Withdrawal failed:', err)
    }
  }

  const handleSaveInvoice = async () => {
    try {
      await api.saveInvoiceData(invoiceData)
    } catch (err) {
      console.error('Failed to save invoice data:', err)
    }
  }

  const formatUSD = (dollars: number) => `$${Number(dollars).toFixed(2)}`

  const bonus = getBonus(depositDollars)
  const sliderIndex = DEPOSIT_STEPS.indexOf(depositDollars)

  const getTransactionTypeLabel = (type: Transaction['type']) => {
    switch (type) {
      case 'deposit': return t.walletTypeDeposit
      case 'usage': return t.walletTypeUsage
      case 'leasing_income': return t.walletTypeLeasingIncome
      case 'leasing_charge': return t.walletTypeLeasingCharge
      case 'refund': return t.walletTypeRefund
      case 'withdrawal': return t.walletTypeWithdrawal || 'Withdrawal'
      default: return type
    }
  }

  const getTransactionIcon = (type: Transaction['type']) => {
    switch (type) {
      case 'deposit': return <ArrowDownCircle size={18} className="wallet-tx-icon wallet-tx-icon--income" />
      case 'usage': return <ArrowUpCircle size={18} className="wallet-tx-icon wallet-tx-icon--charge" />
      case 'leasing_income': return <TrendingUp size={18} className="wallet-tx-icon wallet-tx-icon--income" />
      case 'leasing_charge': return <TrendingDown size={18} className="wallet-tx-icon wallet-tx-icon--charge" />
      case 'refund': return <ArrowDownCircle size={18} className="wallet-tx-icon wallet-tx-icon--income" />
      case 'withdrawal': return <ArrowUpCircle size={18} className="wallet-tx-icon wallet-tx-icon--charge" />
      default: return <Clock size={18} className="wallet-tx-icon" />
    }
  }

  const isIncome = (type: Transaction['type']) =>
    type === 'deposit' || type === 'leasing_income' || type === 'refund'

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  }

  if (loading) {
    return (
      <div className="wallet">
        <h1><Wallet size={24} /> {t.walletTitle}</h1>
        <p className="wallet-loading">{t.loading}</p>
      </div>
    )
  }

  return (
    <div className="wallet">
      <h1 className="wallet-title">
        <Wallet size={24} /> {t.walletTitle}
      </h1>

      {/* Balance Card */}
      <div className="wallet-balance-card">
        <div className="wallet-balance-main">
          <span className="wallet-balance-label">{t.walletBalance}</span>
          <span className="wallet-balance-amount">{wallet ? formatUSD(wallet.balance) : '$0.00'}</span>
        </div>
        <div className="wallet-balance-stats">
          <div className="wallet-stat">
            <TrendingUp size={16} className="wallet-stat-icon wallet-stat-icon--income" />
            <div className="wallet-stat-info">
              <span className="wallet-stat-label">{t.walletTotalDeposited}</span>
              <span className="wallet-stat-value wallet-stat-value--income">
                {wallet ? formatUSD(wallet.total_deposited) : '$0.00'}
              </span>
            </div>
          </div>
          <div className="wallet-stat">
            <TrendingDown size={16} className="wallet-stat-icon wallet-stat-icon--charge" />
            <div className="wallet-stat-info">
              <span className="wallet-stat-label">{t.walletTotalSpent}</span>
              <span className="wallet-stat-value wallet-stat-value--charge">
                {wallet ? formatUSD(wallet.total_spent) : '$0.00'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs: Deposit / Withdraw / Invoice */}
      <div className="wallet-action-tabs">
        <button className={`wallet-action-tab ${activeTab === 'deposit' ? 'active' : ''}`}
          onClick={() => setActiveTab('deposit')}>
          <CreditCard size={16} /> {t.walletDeposit}
        </button>
        <button className={`wallet-action-tab ${activeTab === 'withdraw' ? 'active' : ''}`}
          onClick={() => setActiveTab('withdraw')}>
          <ArrowUpCircle size={16} /> {t.walletWithdraw || 'Withdraw'}
        </button>
        <button className={`wallet-action-tab ${activeTab === 'invoice' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoice')}>
          <FileText size={16} /> {t.walletInvoice || 'Invoice'}
        </button>
      </div>

      {/* Deposit Tab */}
      {activeTab === 'deposit' && (
        <div className="wallet-deposit-card">
          <h2 className="wallet-section-title">
            <DollarSign size={18} /> {t.walletDeposit}
          </h2>

          <div className="wallet-slider-section">
            <div className="wallet-slider-header">
              <span className="wallet-slider-amount">${depositDollars}</span>
              {bonus > 0 && (
                <span className="wallet-slider-bonus">+${bonus} bonus!</span>
              )}
            </div>
            <input
              type="range"
              className="wallet-slider"
              min={0}
              max={DEPOSIT_STEPS.length - 1}
              value={sliderIndex >= 0 ? sliderIndex : 0}
              onChange={(e) => setDepositDollars(DEPOSIT_STEPS[parseInt(e.target.value)])}
            />
            <div className="wallet-slider-labels">
              <span>$10</span>
              <span>$100</span>
              <span>$500</span>
              <span>$1000</span>
            </div>
            {bonus > 0 && (
              <p className="wallet-bonus-info">
                {t.walletBonusInfo || `You'll receive $${depositDollars + bonus} total ($${bonus} bonus)`}
              </p>
            )}
          </div>

          <button
            className="wallet-deposit-btn wallet-deposit-btn--full"
            onClick={handleDeposit}
            disabled={depositing}
          >
            {depositing ? t.loading : `${t.walletDepositBtn} $${depositDollars}${bonus > 0 ? ` (+$${bonus})` : ''}`}
          </button>
        </div>
      )}

      {/* Withdraw Tab */}
      {activeTab === 'withdraw' && (
        <div className="wallet-deposit-card">
          <h2 className="wallet-section-title">
            <ArrowUpCircle size={18} /> {t.walletWithdraw || 'Withdraw Funds'}
          </h2>
          <p className="wallet-withdraw-info">
            {t.walletWithdrawInfo || 'Minimum withdrawal: $10. Funds will be transferred to your payment method.'}
          </p>
          <div className="wallet-deposit-form">
            <div className="wallet-input-wrapper">
              <span className="wallet-input-prefix">$</span>
              <input
                type="number"
                className="wallet-deposit-input"
                placeholder="0.00"
                min="10"
                step="1"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
              />
            </div>
            <button
              className="wallet-deposit-btn"
              onClick={handleWithdraw}
              disabled={!withdrawAmount || parseFloat(withdrawAmount) < 10 || parseFloat(withdrawAmount) > (wallet?.balance || 0)}
            >
              {t.walletWithdrawBtn || 'Withdraw'}
            </button>
          </div>
          {wallet && (
            <p className="wallet-withdraw-available">
              {t.walletAvailable || 'Available'}: {formatUSD(wallet.balance)}
            </p>
          )}
        </div>
      )}

      {/* Invoice Tab */}
      {activeTab === 'invoice' && (
        <div className="wallet-deposit-card">
          <h2 className="wallet-section-title">
            <FileText size={18} /> {t.walletInvoiceData || 'Invoice Data'}
          </h2>
          <div className="wallet-invoice-form">
            <label className="wallet-invoice-check">
              <input
                type="checkbox"
                checked={invoiceData.wantInvoice}
                onChange={(e) => setInvoiceData({ ...invoiceData, wantInvoice: e.target.checked })}
              />
              <span>{t.walletWantInvoice || 'I want a VAT invoice'}</span>
            </label>

            {invoiceData.wantInvoice && (
              <div className="wallet-invoice-fields">
                <label className="wallet-invoice-field">
                  <span>{t.walletCompanyName || 'Company name'}</span>
                  <input type="text" value={invoiceData.companyName}
                    onChange={(e) => setInvoiceData({ ...invoiceData, companyName: e.target.value })} />
                </label>
                <label className="wallet-invoice-field">
                  <span>{t.walletVatNumber || 'EU VAT Number (NIP)'}</span>
                  <input type="text" value={invoiceData.vatNumber} placeholder="PL1234567890"
                    onChange={(e) => setInvoiceData({ ...invoiceData, vatNumber: e.target.value })} />
                </label>
                <label className="wallet-invoice-field">
                  <span>{t.walletAddress || 'Address'}</span>
                  <input type="text" value={invoiceData.address}
                    onChange={(e) => setInvoiceData({ ...invoiceData, address: e.target.value })} />
                </label>
                <div className="wallet-invoice-row">
                  <label className="wallet-invoice-field">
                    <span>{t.walletCity || 'City'}</span>
                    <input type="text" value={invoiceData.city}
                      onChange={(e) => setInvoiceData({ ...invoiceData, city: e.target.value })} />
                  </label>
                  <label className="wallet-invoice-field">
                    <span>{t.walletPostalCode || 'Postal code'}</span>
                    <input type="text" value={invoiceData.postalCode}
                      onChange={(e) => setInvoiceData({ ...invoiceData, postalCode: e.target.value })} />
                  </label>
                </div>
                <label className="wallet-invoice-field">
                  <span>{t.walletCountry || 'Country'}</span>
                  <input type="text" value={invoiceData.country}
                    onChange={(e) => setInvoiceData({ ...invoiceData, country: e.target.value })} />
                </label>
                <button className="button-primary" onClick={handleSaveInvoice}>
                  {t.save}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transaction History */}
      <div className="wallet-transactions-card">
        <h2 className="wallet-section-title">
          <Clock size={18} /> {t.walletTransactions}
        </h2>
        {transactions.length === 0 ? (
          <p className="wallet-no-transactions">{t.walletNoTransactions}</p>
        ) : (
          <div className="wallet-tx-list">
            {transactions.map((tx) => (
              <div key={tx.id} className="wallet-tx-row">
                <div className="wallet-tx-left">
                  {getTransactionIcon(tx.type)}
                  <div className="wallet-tx-details">
                    <span className="wallet-tx-type">{getTransactionTypeLabel(tx.type)}</span>
                    <span className="wallet-tx-desc">{tx.description}</span>
                  </div>
                </div>
                <div className="wallet-tx-right">
                  <span className={`wallet-tx-amount ${isIncome(tx.type) ? 'wallet-tx-amount--income' : 'wallet-tx-amount--charge'}`}>
                    {isIncome(tx.type) ? '+' : '-'}{formatUSD(Math.abs(tx.amount))}
                  </span>
                  <span className="wallet-tx-balance">{formatUSD(tx.balance_after)}</span>
                  <span className="wallet-tx-date">{formatDate(tx.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
